import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

export default function LoginPage() {
    const { login, user } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (user) {
            const from = location.state?.from?.pathname || "/admin";
            navigate(from, { replace: true });
        }
    }, [user, navigate, location]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Welcome back");
            const from = location.state?.from?.pathname || "/admin";
            navigate(from, { replace: true });
        } catch (e) {
            const msg = formatApiErrorDetail(e?.response?.data?.detail) || "Login failed";
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0B1220] flex items-center justify-center px-4">
            <div className="absolute inset-0 bg-grid opacity-20 pointer-events-none" />
            <div className="relative w-full max-w-md">
                <Link to="/" className="flex items-center gap-3 mb-10 justify-center">
                    <div className="h-10 px-2 flex items-center justify-center rounded-md bg-white"><img src={require("../../assets/logo-tcp-mark.png")} alt="The Context Project" className="h-7 w-auto select-none" draggable={false} /></div>
                    <div>
                        <div className="text-base font-bold text-white">The Context Project</div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B]">Admin Console</div>
                    </div>
                </Link>

                <div className="bg-[#111827] border border-[#243044] rounded-2xl p-8">
                    <h1 className="text-2xl font-bold text-white mb-2">Sign in</h1>
                    <p className="text-sm text-[#94A3B8] mb-8">Editorial admin access only.</p>

                    <form onSubmit={onSubmit} className="space-y-5">
                        <div>
                            <label className="block text-xs uppercase tracking-wider text-[#94A3B8] mb-2">Email</label>
                            <input
                                data-testid="login-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoComplete="email"
                                className="w-full bg-[#0B1220] border border-[#243044] rounded-lg px-4 py-2.5 text-sm text-white outline-none focus:border-[#2F80ED] transition-colors"
                                placeholder="hello@thecontextproject.com"
                            />
                        </div>
                        <div>
                            <label className="block text-xs uppercase tracking-wider text-[#94A3B8] mb-2">Password</label>
                            <input
                                data-testid="login-password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                autoComplete="current-password"
                                className="w-full bg-[#0B1220] border border-[#243044] rounded-lg px-4 py-2.5 text-sm text-white outline-none focus:border-[#2F80ED] transition-colors"
                            />
                        </div>

                        {error && (
                            <div data-testid="login-error" className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                                {error}
                            </div>
                        )}

                        <button
                            data-testid="login-submit"
                            type="submit"
                            disabled={loading}
                            className="w-full px-5 py-2.5 rounded-lg bg-[#2F80ED] hover:bg-[#2F80ED]/90 text-white text-sm font-medium transition-colors disabled:opacity-50"
                        >
                            {loading ? "Signing in…" : "Sign in"}
                        </button>
                    </form>
                </div>

                <p className="text-center text-xs text-[#64748B] mt-6">
                    <Link to="/" className="hover:text-[#56CCF2]">← Back to library</Link>
                </p>
            </div>
        </div>
    );
}
